/**
 * Consolidated path constants for the Post-Acute application.
 *
 * These absolute paths are used for:
 *   - <Link to={...}> navigation
 *   - <Navigate to={...}> redirects
 *   - useNavigate() programmatic navigation
 *   - pathname comparisons (e.g. location.pathname.includes(...))
 *
 * Route definitions in routes.tsx use relative paths instead.
 */

export const PortalPaths = {
    Dashboard: {
        Index: '/',
    },
};

export const AdminPaths = {
    Home: {
        Index: '/admin',
    },

    Users: {
        Index: '/admin/users',
        Create: '/admin/users/create',
        Edit: '/admin/users/edit',
    },

    Imports: {
        Snomed: '/admin/imports/snomed',
        LcdWoundIcds: '/admin/imports/lcd-wound-icd',
        PatientIntakes: '/admin/imports/patient-intakes',
        Autobadge: '/admin/imports/autobadge',
        CptRvuValues: '/admin/imports/cpt-rvu-values',
    },

    EventLogs: {
        Index: '/admin/event-logs',
    },

    AdminTools: {
        Index: '/admin/admin-tools',
    },

    CcdaExchange: {
        Index: '/admin/ccda-exchange',
    },

    DesignSystem: {
        Index: '/admin/design-system',
    },

    QualityReporting: {
        Hub: '/admin/quality-reporting',
        Exports: '/admin/quality-reporting/exports',
        Imports: '/admin/quality-reporting/imports',
        Reports: '/admin/quality-reporting/reports',
        AmcDashboard: '/admin/quality-reporting/amc-dashboard',
        EvidenceBundle: '/admin/quality-reporting/evidence-bundle',
        Filter: '/admin/quality-reporting/filter',
        MipsPi: '/admin/quality-reporting/mips-pi',
    },

    CqmEnablement: {
        Index: '/admin/cqm-enablement',
    },

    CqmMeasureConfiguration: {
        Index: '/admin/cqm-measure-configuration',
    },

    DsiInterventions: {
        Index: '/admin/dsi-interventions',
    },

    SmartApps: {
        Index: '/admin/smart-apps',
    },
};

export const PostAcuteAdminPaths = {
    Home: {
        Index: '/admin/postacute',
    },
    Regions: {
        Index: '/admin/regions',
        Create: '/admin/regions/create',
        Edit: '/admin/regions/edit',
    },

    Facilities: {
        Index: '/admin/facilities',
        Create: '/admin/facilities/create',
        Edit: '/admin/facilities/edit',
    },

    PatientTypes: {
        Index: '/admin/patient-types',
        Create: '/admin/patient-types/create',
        Edit: '/admin/patient-types/edit',
    },

    ArrivingFroms: {
        Index: '/admin/arriving-froms',
        Create: '/admin/arriving-froms/create',
        Edit: '/admin/arriving-froms/edit',
    },

    ReturnToHospital: {
        Index: '/admin/return-to-hospital',
    },

    HospitalSystems: {
        Index: '/admin/hospital-systems',
        Create: '/admin/hospital-systems/create',
        Edit: '/admin/hospital-systems/edit',
    },

    VisitAdmin: {
        Index: '/admin/visits',
    },

    HospiceCompanies: {
        Index: '/admin/hospice-companies',
        Create: '/admin/hospice-companies/create',
        Edit: '/admin/hospice-companies/edit',
    },
};

export const BedBoardAdminPaths = {
    Hospitals: {
        Index: '/admin/hospitals',
        Create: '/admin/hospitals/create',
        Edit: '/admin/hospitals/edit',
    },
};

export const PostAcutePaths = {
    Home: {
        Index: '/postacute',
    },

    Viewer: {
        Index: '/postacute/viewer',
    },

    Admin: {
        Index: '/postacute/admin',
    },

    Abandoned: {
        Index: '/postacute/abandoned',
    },

    ReturnToHospital: {
        Index: '/postacute/return-to-hospital',
        Form: '/postacute/return-to-hispital/form',
    },

    ClinicalCoordinator: {
        Index: '/postacute/clinical-coordinator',
    },

    Notes: {
        Edit: '/postacute/note/edit',
    },

    StagedNotes: {
        Edit: '/postacute/staged-note/edit',
    },

    UnsignedNotes: {
        Index: '/postacute/unsigned-notes',
        Print: '/postacute/unsigned-notes/print',
    },

    QuickNotes: {
        Index: '/postacute/quick-notes',
    },

    PccCensus: {
        Index: '/postacute/pcc-census',
    },

    RouteBuilder: {
        Index: '/postacute/route-builder',
    },

    Schedule: {
        Index: '/postacute/schedule',
    },

    PrivacyPolicy: {
        Index: '/postacute/privacy-policy',
    },

    Reporting: {
        Productivity: '/postacute/reporting/productivity',
    },

    NewAdmits: {
        Index: '/postacute/new-admits',
    },
};

export const CensusPaths = {
    Home: {
        Index: '/postacute/census',
    },

    Notes: {
        Print: '/postacute/census/notes/print',
    },

    QuickNotes: {
        Print: '/postacute/census/quicknotes/print',
    },

    ReturnToHospital: {
        Print: '/postacute/census/returntohospital/print',
    },
};

export const PccCensusPaths = {
    Home: {
        Index: '/postacute/pcc-census',
    },
};

export const MatrixCareCensusPaths = {
    Home: {
        Index: '/postacute/matrix-care-census',
    },
};

export const ClinicalCoordinatorPaths = {
    Index: '/postacute/clinical-coordinator',
    MedicalSummaryList: '/postacute/clinical-coordinator/medical-summary-list',
    MedicalSummary: '/postacute/clinical-coordinator/medical-summary',
    FaceSheets: '/postacute/clinical-coordinator/face-sheets',
    AnnualWellnessVisit: '/postacute/clinical-coordinator/annual-wellness',
    AwvForm: '/postacute/clinical-coordinator/awv-form',
};

export const ImplantableDevicesPaths = {
    /** Build the concrete URL for a specific intake (patient-scoped management page, SCRUM-795). */
    manage: (intakeId: number) => `/postacute/implantable-devices/${intakeId}`,
};

export const ErxPaths = {
    /** Build the concrete URL for a specific intake (embedded ScriptSure launch page, SCRUM-801). */
    launch: (intakeId: number) => `/postacute/erx/${intakeId}`,

    /** Embedded ScriptSure pending-messages widget page (SCRUM-805). */
    Messages: '/postacute/erx/messages',
};

export const SchedulePaths = {
    Home: {
        Index: '/postacute/schedule',
        Print: '/postacute/schedule/print',
    },
};

export const RouteBuilderPaths = {
    Home: {
        Index: '/postacute/route-builder',
    },
};

export const ReportingPaths = {
    Dashboard: {
        Index: '/reporting',
    },

    Productivity: {
        Index: '/reporting/productivity',
    },
};
